import React, { useState } from 'react'
import domPurify from 'dompurify'
import moment, { Moment } from 'moment'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  TextField,
} from '@mui/material'
import { Button } from '@resiliatech/birdsong'
import MenuItem from '@mui/material/MenuItem'
import {
  CreateGoalGroupInput,
  GoalCategory,
  GoalGroupCard,
} from 'Generated/graphql-types'
import { categories } from 'TypeDefs/Goal'
import { funderGoals } from 'Modules/Analytics/trackActionInterface/FunderPlatform/funderGoals'
import { analytics } from 'Modules/Analytics'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  ButtonsContainer,
  GoalGroupInputsContainer,
} from './GoalGroupInputs.styled'
import {
  CategorySelector,
  DatePickers,
} from '../../../Impact/GoalsDashboard/GoalModal/GoalModal.styled'
import { DatePicker } from '../../../../SharedComponents/DatePicker/DatePicker'
import { GOAL_DATE_FORMAT } from '../../../Impact/GoalsDashboard/types'
import { getUpdatedGoalGroupFields } from '../utils'

type GoalGroupInputsProps = {
  disableTargetFields?: boolean
  goalGroup?: GoalGroupCard
  isLoading?: boolean
  onCancel?: () => void
  onCancelLabel: string
  onSubmit: (goalGroup: CreateGoalGroupInput) => Promise<boolean>
  onSubmitLabel: string
}

export const GoalGroupInputs = ({
  disableTargetFields = true,
  goalGroup,
  isLoading = false,
  onCancel,
  onCancelLabel,
  onSubmit,
  onSubmitLabel,
}: GoalGroupInputsProps) => {
  const {
    userConfig: { id, email },
  } = useAppState()
  const [title, setTitle] = useState(goalGroup?.title || '')
  const [description, setDescription] = useState(goalGroup?.description || '')
  const [category, setCategory] = useState<GoalCategory>(
    goalGroup?.category || GoalCategory.None
  )
  const [startDate, setStartDate] = useState<Moment | null>(
    goalGroup?.startDate ? moment(goalGroup?.startDate) : null
  )

  const [endDate, setDueDate] = useState<Moment | null>(
    goalGroup?.endDate ? moment(goalGroup?.endDate) : null
  )
  const [target, setTarget] = useState(
    goalGroup?.target ? String(goalGroup?.target) : ''
  )
  const [targetDescription, setTargetDescription] = useState(
    goalGroup?.targetDescription || ''
  )
  const [isFunderAssigned, setIsFunderAssigned] = useState(false)
  const [isCategorySelectOpen, setIsCategorySelectOpen] = useState(false)
  const categoryOptions = Object.entries(categories)
    .filter(([cat]) => cat !== GoalCategory.None)
    .map(([cat, displayValue]) => (
      <MenuItem key={cat} value={cat}>
        {displayValue}
      </MenuItem>
    ))

  const resetGoalGroupInputs = () => {
    setTitle('')
    setDescription('')
    setTarget('')
    setTargetDescription('')
    setCategory(GoalCategory.None)
    setStartDate(null)
    setDueDate(null)
    setIsFunderAssigned(false)
  }
  const handleCancelGoalGroup = () => {
    resetGoalGroupInputs()
    onCancel?.()
  }

  const dispatchGoalGroupUpdatedEvent = (updatedFields: string[]) => {
    analytics.trackActionCreator(funderGoals.Funder_Collab_Goal_Edit_Saved)({
      userId: id,
      userEmail: email,
      updatedFields,
    })
  }

  const handleSubmitGoalGroup = async () => {
    const updatedFields = getUpdatedGoalGroupFields(
      {
        title: goalGroup?.title,
        description: goalGroup?.description,
        category: goalGroup?.category,
        startDate: goalGroup?.startDate ?? null,
        endDate: goalGroup?.endDate ?? null,
        target: goalGroup?.target === null ? '' : String(goalGroup?.target),
        targetDescription: goalGroup?.targetDescription,
        isFunderAssigned: goalGroup?.isFunderAssigned,
      },
      {
        title,
        description,
        category,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        target,
        targetDescription,
        isFunderAssigned,
      }
    )

    if (updatedFields.length > 0) {
      dispatchGoalGroupUpdatedEvent(updatedFields)
    }

    await onSubmit({
      title,
      description,
      category,
      startDate,
      endDate,
      target: target ? Number(target) : null,
      targetDescription,
      isFunderAssigned,
    })
  }

  const isCreateGoalGroupButtonDisabled =
    !title || !category || category === GoalCategory.None

  const CATEGORY_SELECT_LABEL = 'Categorize this goal'

  return (
    <GoalGroupInputsContainer>
      <TextField
        id='goalGroupTitle'
        label='Name this goal'
        onChange={({ target: { value } }) => setTitle(value)}
        required
        size='small'
        value={domPurify.sanitize(title)}
        variant='outlined'
      />
      <TextField
        id='goalGroupDescription'
        label='Describe this goal'
        multiline
        onChange={({ target: { value } }) => setDescription(value)}
        size='small'
        value={domPurify.sanitize(description)}
        variant='outlined'
      />
      <Box display='flex' gap='24px'>
        <TextField
          disabled={disableTargetFields}
          id='goalGroupTarget'
          label='Set a numerical goal target to reach'
          onChange={({ target: { value } }) => setTarget(value)}
          size='small'
          value={domPurify.sanitize(target)}
          variant='outlined'
          sx={{ flex: '1' }}
        />
        <TextField
          disabled={disableTargetFields}
          id='goalGroupTargetDescription'
          label='Describe what the numerical target represents'
          onChange={({ target: { value } }) => setTargetDescription(value)}
          size='small'
          value={domPurify.sanitize(targetDescription)}
          variant='outlined'
          sx={{ flex: '1' }}
        />
      </Box>
      <FormControl fullWidth sx={{ display: 'flex', gap: '20px' }}>
        <InputLabel id='goalGroupCategorySelectLabel' required>
          {CATEGORY_SELECT_LABEL}
        </InputLabel>
        <Select
          id='goalGroupCategory'
          displayEmpty
          label={CATEGORY_SELECT_LABEL}
          labelId='goalGroupCategorySelectLabel'
          value={category}
          onChange={({ target: { value } }) =>
            setCategory(value as GoalCategory)
          }
          onClose={() => setIsCategorySelectOpen(false)}
          onOpen={() => setIsCategorySelectOpen(true)}
          open={isCategorySelectOpen}
          required
        >
          <MenuItem value={GoalCategory.None}>
            <CategorySelector>Select an option</CategorySelector>
          </MenuItem>
          {categoryOptions}
        </Select>
      </FormControl>
      <DatePickers>
        <DatePicker
          date={startDate ? startDate.format(GOAL_DATE_FORMAT) : null}
          label='Start Date (Optional)'
          onDateSelected={setStartDate}
          styles={{ width: '50%' }}
        />
        <DatePicker
          date={endDate ? endDate.format(GOAL_DATE_FORMAT) : null}
          label='End Date (Optional)'
          onDateSelected={setDueDate}
          styles={{ width: '50%' }}
        />
      </DatePickers>
      <FormControl fullWidth sx={{ display: 'flex', gap: '20px' }}>
        <FormControlLabel
          control={
            <Checkbox
              onChange={(event) => {
                setIsFunderAssigned(event.target.checked)
              }}
              checked={isFunderAssigned}
            />
          }
          label='This Goal Group is Funder-Assigned'
        />
      </FormControl>
      <ButtonsContainer>
        <Button
          isDisabled={isLoading}
          label={onCancelLabel}
          onClick={handleCancelGoalGroup}
          type='button'
          variant='secondary'
        />
        <Button
          isDisabled={isCreateGoalGroupButtonDisabled}
          isLoading={isLoading}
          label={onSubmitLabel}
          onClick={handleSubmitGoalGroup}
          type='button'
          variant='secondary'
        />
      </ButtonsContainer>
    </GoalGroupInputsContainer>
  )
}
